import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import axios from 'axios';
import Login from './components/Login.jsx';
import Sidebar from './components/Sidebar.jsx';

// ✅ OPTIMIZACIÓN: Lazy loading de componentes (se cargan bajo demanda)
// Reduce el bundle inicial de 2.5MB a ~600KB
const CrearCliente = lazy(() => import('./Modules/Clientes/Pages/CrearCliente.jsx'));
const Cotizaciones = lazy(() => import('./Modules/Cotizaciones/Pages/Cotizaciones.jsx'));
const CrearRemision = lazy(() => import('./Modules/Remisiones/Pages/CrearRemision.jsx'));
const ConsultarRemision = lazy(() => import('./Modules/Remisiones/Pages/ConsultarRemision.jsx'));
const AnularRemision = lazy(() => import('./Modules/Remisiones/Pages/AnularRemision.jsx'));
const IngresarPago = lazy(() => import('./Modules/Pagos/Pages/IngresarPagos.jsx'));
const VerificarPagos = lazy(() => import('./Modules/Pagos/Pages/VerificarPagos.jsx'));
const Facturacion = lazy(() => import('./Modules/Facturacion/Pages/Facturas.jsx'));
const FacturasContadoSinPago = lazy(() => import('./Modules/Facturacion/Pages/FacturasContadoSinPago.jsx'));
const Alistamientos = lazy(() => import('./Modules/Alistamientos/Pages/Alistamientos.jsx'));
const OrdenesCompra = lazy(() => import('./Modules/OrdenesCompra/Pages/OrdenesCompra.jsx'));
const Traslados = lazy(() => import('./Modules/Traslados/Pages/Traslados.jsx'));
const TrasladoPedidos = lazy(() => import('./Modules/Traslados/Pages/TrasladosPedidos.jsx'));
const Inventario = lazy(() => import('./Modules/Inventario/Pages/Inventario.jsx'));
const ConsultarSeriales = lazy(() => import('./Modules/Seriales/Pages/ConsultarSeriales'));
const RedimirPuntos = lazy(() => import('./Modules/Puntos/Pages/RedimirPuntos.jsx'));
const Permisos = lazy(() => import('./Modules/Permisos/Pages/Permisos.jsx'));
const MiPerfil = lazy(() => import('./Modules/Perfil/Pages/MiPerfil.jsx'));
const Reportes = lazy(() => import('./Modules/Reportes/Pages/Reportes.jsx'));
const DetalleReporte = lazy(() => import('./Modules/Reportes/Pages/DetalleReporte.jsx'));
const Home = lazy(() => import('./Modules/Homepage/Pages/Home.jsx'));

// ✅ Componente de carga mientras se obtiene el componente lazy
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div>Cargando...</div>
  </div>
);

// CONFIGURACIÓN DEL INTERCEPTOR DE AXIOS
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.redirectToLogin) {
      console.log('🔐 Token inválido o expirado, redirigiendo al login...');
      // Limpiar todo el almacenamiento local
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userData');
      localStorage.removeItem('userPermissions');
      sessionStorage.clear();
      // Forzar redirección al login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// También agregar el token automáticamente a cada request
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const ROLES = {
  ADMIN: 1,
  FINANCIAL: 2,
  SALES: 3,
  WAREHOUSE: 4,
  AUXILIAR: 5
};

// Componente para rutas protegidas por autenticación
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Componente para rutas protegidas por rol
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const userRole = parseInt(localStorage.getItem('userRole'));
  const location = useLocation();
  let permissions = null;
  try {
    const storedPermissions = localStorage.getItem('userPermissions');
    permissions = storedPermissions ? JSON.parse(storedPermissions) : null;
  } catch {
    permissions = null;
  }

  const pathPermissions = {
    '/clientes': 'clientes',
    '/cotizaciones': 'cotizaciones',
    '/remisiones': 'remisiones',
    '/remisiones/crear-remision': 'remisiones.crear',
    '/remisiones/consultar-remision': 'remisiones.consultar',
    '/remisiones/anular': 'remisiones.anular',
    '/pagos/ingresar-pago': 'pagos.registrar',
    '/pagos/verificar': 'pagos.verificar',
    '/facturacion': 'facturacion.emitir',
    '/facturas-contado-sin-pago': 'facturacion.contado',
    '/bodega/alistamientos': 'bodega.alistamientos',
    '/bodega/ordenes-compra': 'bodega.ordenes_compra',
    '/bodega/traslados-mercancia': 'bodega.traslados',
    '/bodega/traslados': 'bodega.traslados',
    '/bodega/traslados-pedidos': 'bodega.traslados_pedidos',
    '/bodega/inventario': 'bodega.serializar',
    '/bodega/seriales': 'bodega.consultar_serial',
    '/redimir-puntos': 'puntos',
    '/permisos': 'permisos',
  };
  const permissionKey = location.pathname === '/reportes' || location.pathname.startsWith('/reportes/')
    ? 'reportes'
    : pathPermissions[location.pathname];
  
  console.log('🛡️ RoleProtectedRoute - Verificando acceso:');
  console.log('   - Rol usuario:', userRole);
  console.log('   - Roles permitidos:', allowedRoles);
  console.log('   - ¿Tiene acceso?', allowedRoles.includes(userRole));
  
  if (permissions && permissionKey && Object.prototype.hasOwnProperty.call(permissions, permissionKey)) {
    return permissions[permissionKey] === true
      ? children
      : <Navigate to="/home" replace />;
  }

  if (!allowedRoles.includes(userRole)) {
    console.log('   - ❌ Acceso denegado, redirigiendo a /home');
    return <Navigate to="/home" replace />;
  }
  
  console.log('   - ✅ Acceso permitido');
  return children;
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Ruta pública */}
        <Route path="/login" element={<Login />} />
        
        {/* Rutas protegidas */}
        <Route path="/home" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Sidebar><Home /></Sidebar>
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/reportes" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <RoleProtectedRoute allowedRoles={Object.values(ROLES)}>
                <Sidebar><Reportes /></Sidebar>
              </RoleProtectedRoute>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/reportes/:id" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <RoleProtectedRoute allowedRoles={Object.values(ROLES)}>
                <Sidebar><DetalleReporte /></Sidebar>
              </RoleProtectedRoute>
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/mi-perfil" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Sidebar><MiPerfil /></Sidebar>
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/clientes" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><CrearCliente /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/cotizaciones" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Cotizaciones /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/remisiones" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><CrearRemision /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/remisiones/crear-remision" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><CrearRemision /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/remisiones/consultar-remision" element={
            <ProtectedRoute>
              <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
                <Suspense fallback={<LoadingFallback />}>
                  <Sidebar><ConsultarRemision /></Sidebar>
                </Suspense>
              </RoleProtectedRoute>
            </ProtectedRoute>
        } />

        <Route path="/remisiones/anular" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><AnularRemision /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/pagos/ingresar-pago" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.FINANCIAL, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><IngresarPago /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/pagos/verificar" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.FINANCIAL, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><VerificarPagos /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />
        
        <Route path="/facturacion" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.FINANCIAL]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Facturacion /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/facturas-contado-sin-pago" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.FINANCIAL]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><FacturasContadoSinPago /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />
        
        <Route path="/bodega/alistamientos" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE, ROLES.SALES, ROLES.AUXILIAR]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Alistamientos /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/bodega/ordenes-compra" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><OrdenesCompra /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/bodega/traslados" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Traslados /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/bodega/traslados-mercancia" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Traslados /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/bodega/traslados-pedidos" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><TrasladoPedidos /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/bodega/inventario" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Inventario /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/bodega/seriales" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.WAREHOUSE]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><ConsultarSeriales /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/redimir-puntos" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SALES]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><RedimirPuntos /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/permisos" element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <Suspense fallback={<LoadingFallback />}>
                <Sidebar><Permisos /></Sidebar>
              </Suspense>
            </RoleProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Redirecciones */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
